import { Component, OnInit, OnDestroy, EventEmitter, Output, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../core/services/language.service';
import { UserService } from '../../core/services/user.service';
import { EventService } from '../../core/services/event.service';
import { HotelService } from '../../core/services/hotel.service';
import { DialogService } from '../../core/services/dialog.service';
import { LoggerService } from '../../core/services/logger.service';
import { ImpersonationService } from '../../core/services/impersonation.service';
import { User } from '../../core/interfaces/user';
import { Profile } from '../../core/interfaces/profile';
import { Hotel } from '../../core/interfaces/hotel';
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subscription, take } from 'rxjs';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    standalone: false
})
export class TopbarComponent implements OnInit, OnDestroy {
  flagvalue: string | string[] | undefined;
  cookieValue: string | undefined;
  valueset: string | undefined;
  user: User = new User();
  profile: Profile = new Profile();
  public managedHotel: Hotel | null = null;
  pendingCount = 0;
  isImpersonating = false;
  impersonatedUserName = '';
  private hotelSubscription?: Subscription;
  private impersonationSubscription?: Subscription;
  private userChangedSubscription?: Subscription;
  private readonly document = inject(DOCUMENT);

  @Output() mobileMenuButtonClicked = new EventEmitter();
  @Output() settingsButtonClicked = new EventEmitter();

  constructor(
    private router: Router,
    public languageService: LanguageService,
    public _cookiesService: CookieService,
    public translate: TranslateService,
    private userService: UserService,
    private eventService: EventService,
    private hotelService: HotelService,
    private dialogService: DialogService,
    private snackBar: MatSnackBar,
    private logger: LoggerService,
    private impersonationService: ImpersonationService,
  ) {
    // Language is now managed by LanguageService
    // It handles persistence via cookie and localStorage
  }

  ngOnInit(): void {
    this.user = this.userService.getUser();

    // Get current language from service
    this.cookieValue = this.languageService.getCurrentLanguage();
    const val = this.listLang.filter(x => x.lang === this.cookieValue);
    if (val.length === 0) {
      if (this.flagvalue === undefined) {
        this.valueset = 'assets/images/flags/translations.svg';
      }
    } else {
      this.flagvalue = val.map(element => element.flag);
    }

    // We deliberately do NOT seed the language from the cached user
    // here. The user object in the cookie can be stale (e.g. the locale
    // was just changed on another device or in a previous PATCH that
    // hasn't been reflected back into the cached cookie yet). Acting on
    // it would burn the LanguageService's one-shot backend-locale slot
    // on a stale value, blocking the fresh /me response from applying.
    // The freshly fetched /me drives the language via the userChanged
    // handler below.

    // Subscribe to hotel changes (continues to receive updates, not just first emission)
    this.hotelSubscription = this.hotelService.selectedHotel$.subscribe((hotel: Hotel | null) => {
      this.managedHotel = hotel;
      this.document.body.classList.toggle('hotel-archived', !!hotel?.is_archived);
    });

    // Subscribe to impersonation state to show impersonated user in header
    this.impersonationSubscription = this.impersonationService.impersonationState$.subscribe(state => {
      this.isImpersonating = state !== null;
      this.impersonatedUserName = state?.targetUserName || '';
    });

    // Apply the freshly fetched backend locale (only once per session —
    // the LanguageService guards against subsequent /me responses
    // overriding a user choice that's still PATCHing).
    this.userChangedSubscription = this.eventService.subscribe('userChanged', (payload: unknown) => {
      if (payload) {
        this.user = payload as User;
        if (this.user?.locale) {
          this.languageService.applyBackendLocale(this.user.locale);
          this.cookieValue = this.languageService.getCurrentLanguage();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.hotelSubscription?.unsubscribe();
    this.impersonationSubscription?.unsubscribe();
    this.userChangedSubscription?.unsubscribe();
    this.document.body.classList.remove('hotel-archived');
  }

  openHotelsDialog(): void {
    this.dialogService.openHotelSelectionDialog()
      .afterClosed()
      .pipe(take(1))
      .subscribe((hotelId: string | null) => {
        if (hotelId) {
          this.router.navigate(['/', hotelId, 'dashboard']);
        }
      });
  }

  copyShareLink(): void {
    if (!this.managedHotel) {
      this.snackBar.open('Please select a hotel before sharing.', 'OK', { duration: 3000 });
      return;
    }

    const shareUrl = `${window.location.origin}${this.router.url}`;

    navigator.clipboard.writeText(shareUrl).then(() => {
      this.snackBar.open('Share URL copied to clipboard!', 'OK', { duration: 3000, politeness: 'polite' });
    }).catch(err => {
      this.logger.error('Failed to copy URL: ', err);
      this.snackBar.open('Share URL copied to clipboard!', '', { duration: 3000, politeness: 'polite' });
    });
  }

  openCardValidityDialog(): void {
    this.dialogService.openCardValidityDialog();
  }


  setLanguage(text: string, lang: string, flag: string): void {
    this.flagvalue = flag;
    this.cookieValue = lang;
    // LanguageService handles persistence to cookie and localStorage
    this.languageService.setLanguage(lang);
  }

  toggleMobileMenu(event: Event): void {
    const currentSidebarSize = document.body.getAttribute('data-sidebar-size');

    if (window.innerWidth >= 992) {
      // `sidebar-enable` is the mobile overlay state. Applying it on desktop
      // raises the topbar above the sidebar and can cover the logo while the
      // sidebar is changing size.
      document.body.classList.remove('sidebar-enable');

      let newSize: string;
      if (currentSidebarSize == null || currentSidebarSize === 'lg') {
        newSize = 'sm';
      } else if (currentSidebarSize === 'md') {
        newSize = 'sm';
      } else {
        newSize = 'lg';
      }
      document.body.setAttribute('data-sidebar-size', newSize);
      // Persist sidebar state
      localStorage.setItem('sidebarSize', newSize);
    } else {
      document.body.classList.toggle('sidebar-enable');
    }
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.userService.logout().subscribe({
      next: () => {
        this.router.navigate(['/account/login']);
      },
      error: () => {
        // Even on error, navigate to login
        this.router.navigate(['/account/login']);
      }
    });
  }

  /**
   * Get the display name for the header.
   * Shows impersonated user name when impersonating, otherwise the actual user name.
   */
  getDisplayUserName(): string {
    if (this.isImpersonating && this.impersonatedUserName) {
      return this.impersonatedUserName;
    }
    return this.user.name || 'User';
  }

  /**
   * Get user's primary role name for display
   */
  getUserRoleName(): string {
    if (this.user.internal_role) {
      // Map slug to display name
      const roleNames: Record<string, string> = {
        'super-admin': 'Super Admin',
        'customer-success': 'Customer Success',
        'viewer-system': 'Viewer'
      };
      return roleNames[this.user.internal_role] || this.user.internal_role;
    }

    return 'Hotel User';
  }

  getSelectedLanguageName(): string {
    const selectedLang = this.languageService.getCurrentLanguage();
    const langItem = this.listLang.find(lang => lang.lang === selectedLang);
    return langItem ? langItem.name : 'English';
  }

  listLang = [
    { translationKey: 'LANGUAGES.GERMAN', name: 'Deutsch', flag: 'assets/images/flags/germany.svg', lang: 'de' },
    { translationKey: 'LANGUAGES.FRENCH', name: 'Français', flag: 'assets/images/flags/france.svg', lang: 'fr' },
    { translationKey: 'LANGUAGES.ITALIAN', name: 'Italiano', flag: 'assets/images/flags/italy.svg', lang: 'it' },
    { translationKey: 'LANGUAGES.ENGLISH', name: 'English', flag: 'assets/images/flags/united-kingdom.svg', lang: 'en' },
  ];
}
